import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  TextField,
} from '@mui/material';
import { CommonDialog, CommonDialogHeader } from '..';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { IRolePermission, IRootState } from '../../../types/store';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { RolesAndPermissionsService } from '../../../api/services/rolesAndPermissionsService';
import { addNewRole, updateRole } from '../../../store/Reducers/RolesAndPermissionsSlice';
import { IRoleContent } from '../../../types/store';
import { RefreshButton } from '../../Buttons/RefreshButton';
import { PermissionsService } from '../../../api/services/permissionsService';
import { setPermissions } from '../../../store/Reducers/PermissionsSlice';
import Tooltip from '@mui/material/Tooltip';
import { ICreateUpdateRoleRequest } from '../../../types/api/request/rolesandpermissionsrequest';
import { ICreateEditRoleDialogProps, ICreateEditRoleDialogBodyProps } from '../../../types/dialog';

export const CreateEditRoleDialog = ({
  selectedRole,
  isOpenDialog,
  setIsOpenDialog,
}: ICreateEditRoleDialogProps) => {
  const dispatch = useDispatch();

  const onSubmit = async (payload: ICreateUpdateRoleRequest) => {
    try {
      const role = selectedRole
        ? await RolesAndPermissionsService.updateRole(payload)
        : await RolesAndPermissionsService.createRole(payload);
      dispatch(selectedRole ? updateRole(role) : addNewRole(role));
      dispatch(
        showNotification({
          message: `Role successfully ${selectedRole ? 'updated' : 'created'}.`,
        }),
      );
      setIsOpenDialog(false);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.response?.data?.message,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      <CommonDialog
        isOpen={isOpenDialog}
        header={
          <CommonDialogHeader
            title={selectedRole ? 'Edit Role' : 'Add New Role'}
            onClose={() => {
              setIsOpenDialog(false);
            }}
          />
        }
        body={<DialogBody onSubmit={onSubmit} selectedRole={selectedRole} />}
        footer={undefined}
      />
    </>
  );
};

const DialogBody = ({ onSubmit, selectedRole = null }: ICreateEditRoleDialogBodyProps) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ICreateUpdateRoleRequest>({
    defaultValues: {
      id: selectedRole ? selectedRole.id : null,
      name: selectedRole ? selectedRole.name : '',
      description: selectedRole ? selectedRole.description : '',
      permission_ids: selectedRole
        ? selectedRole.permissions?.map((x: IRolePermission) => x.id)
        : [],
    },
    mode: 'onChange',
  });

  const dispatch = useDispatch();
  const { content: permissions } = useSelector((state: IRootState) => state.permissions);
  const { content: roles } = useSelector((state: IRootState) => state.rolesAndPermissions);

  const buttonLabel = selectedRole ? 'Update Role' : 'Create Role';
  const loadingLabel = selectedRole ? 'Updating...' : 'Creating...';

  const handleFormSubmit = async (data: ICreateUpdateRoleRequest) => {
    try {
      await onSubmit(data); // call parent onSubmit
      reset(); // reset form fields after success
    } catch {}
  };

  const reloadPermissions = async () => {
    try {
      const permissions = await PermissionsService.getAllPermissions();
      dispatch(setPermissions(permissions));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to retrieve latest list of Permissions. ${e?.response?.data || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
        }}
      >
        {/* Role Name */}
        <FormControl>
          <FormLabel>Name</FormLabel>
          <TextField
            type="text"
            placeholder="Role 01"
            fullWidth
            variant="outlined"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', {
              required: 'Role name is required',
              validate: (value) => {
                const taken = roles?.some(
                  (r: IRoleContent) =>
                    r.id !== selectedRole?.id &&
                    r.name.toLowerCase() === value?.trim().toLowerCase(),
                );
                return taken ? 'A role with this name already exists' : true;
              },
            })}
          />
        </FormControl>

        {/* Description */}
        <FormControl>
          <FormLabel>Description</FormLabel>
          <TextField
            type="text"
            placeholder="This role is for..."
            fullWidth
            variant="outlined"
            error={!!errors.description}
            helperText={errors.description?.message}
            {...register('description', {
              required: 'Description is required',
            })}
          />
        </FormControl>

        {/* Permissions */}
        <FormControl>
          <FormLabel
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            Permissions
            <Tooltip title="Retrieve latest list of Permissions">
              <span>
                <RefreshButton
                  onClick={() => {
                    reloadPermissions();
                  }}
                />
              </span>
            </Tooltip>
          </FormLabel>

          <Controller
            name="permission_ids"
            control={control}
            rules={{ required: 'Please select at least one permission' }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                multiple
                options={permissions || []}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                value={permissions?.filter((p) => value?.includes(p.id)) || []}
                onChange={(e, selectedOptions) =>
                  onChange(selectedOptions.map((option) => option.id))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder="Select permissions" />}
              />
            )}
          />
          {errors.permission_ids && (
            <Box sx={{ color: 'red', mt: 0.5 }}>{errors.permission_ids.message}</Box>
          )}
        </FormControl>

        {/* Submit */}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ backgroundColor: '#000' }}
        >
          {isSubmitting ? loadingLabel : buttonLabel}
        </Button>
      </Box>
    </>
  );
};
